import { Component, OnInit } from '@angular/core';
import { AuthService } from 'src/app/core/services/authentication.service';
import { Global } from 'src/app/core/resources/global';
import { FormGroup, FormBuilder, FormArray, Validators, FormControl, AbstractControl } from '@angular/forms';
import { ManualCreationCargoService } from 'src/app/modules/cargo/manual-creation-cargo/manual-creation-cargo.service';
import * as _ from 'lodash';
import { Router } from '@angular/router';
import { VehicleCargo } from 'src/app/core/interfaces/vehicle-cargo';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { Fmt } from 'src/app/core/messages/fmt';
import { FormMessages } from 'src/app/core/messages/form-messages.enum';
import { Utils } from 'src/app/core/resources/utils';
import { Permission } from 'src/app/core/resources/permission';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { environmentTypes } from 'src/app/core/resources/typeEnvironment';
import { CargoManager } from 'src/app/core/managers/cargo.manager';
import { OptionsAutocomplete } from 'src/app/core/interfaces/optionsAutocomplete';

@Component({
  selector: 'app-quantity-vehicles',
  templateUrl: './quantity-vehicles.component.html',
  styleUrls: ['./quantity-vehicles.component.scss'],
  providers: [AuthService, Global]
})
export class QuantityVehiclesComponent implements OnInit {
  permission = Permission;
  formVehicles: FormGroup = this.formBuilder.group({
    vehicles: this.formBuilder.array([])
  });
  bodyworkOptions: OptionsAutocomplete[] = [];
  trailerOptions: OptionsAutocomplete[] = [];
  formValidate: string = '';
  minQuantity: number = 1;
  minAxles: number = 2;
  subscriptions: Subscription = new Subscription();
  environmentTypes: string[] = environmentTypes;
  constructor(
    private formBuilder: FormBuilder,
    public manualCreationCargoService: ManualCreationCargoService,
    public snackBarService: SnackBarService,
    private router: Router,
    private spinner: NgxSpinnerService,
    public utils: Utils,
    private permissionRole: PermissionRole,
    private cargoManager: CargoManager
  ) {
  }

  ngOnInit() {
    if (!this.manualCreationCargoService.vehiclesSelected || !this.manualCreationCargoService.vehiclesSelected.length) {
      this.stepBack();
      return;
    }
    this.setFormControls();
    this.setSubscriptions();
  }

  setSubscriptions() {
    this.vehiclesFormArray.controls.forEach((control, index) => {
      const group = control as FormGroup;
      const quantityControl = group.get('quantity') as FormControl;
      const bodyworkTypeControl = group.get('bodyworkType') as FormControl;
      const environmentControl = group.get('environment') as FormControl;
      const trailerBodyworkControl = group.get('trailerBodywork') as FormControl;
      const trailerAxlesControl = group.get('trailerAxles') as FormControl;
      const vehicle = this.manualCreationCargoService.vehiclesSelected[index];
      this.subscriptions.add(quantityControl.valueChanges.subscribe(value => {
        const quantity = value ? Number(value) : 0;
        vehicle.quantity = quantity;
        let count = 0;
        const vehiclesWithLicensePlateToKeep = [];
        this.manualCreationCargoService.vehiclesSelectedWithLicensePlate.forEach(vehicleWithLicensePlate => {
          if (vehicleWithLicensePlate.id === vehicle.id) {
            count++;
            if (count <= vehicle.quantity) vehiclesWithLicensePlateToKeep.push(vehicleWithLicensePlate);
          } else vehiclesWithLicensePlateToKeep.push(vehicleWithLicensePlate);
        });
        this.manualCreationCargoService.vehiclesSelectedWithLicensePlate = vehiclesWithLicensePlateToKeep;
      }));
      this.subscriptions.add(bodyworkTypeControl.valueChanges.subscribe(value => {
        vehicle.bodyWorkType = {
          id: value && value.id ? value.id : '',
          name: value && value.name ? value.name : ''
        };
        trailerBodyworkControl.setValue(null, { emitEvent: false });
        trailerAxlesControl.setValue(null, { emitEvent: false });
        vehicle.trailer = { bodywork: '', axles: null };
        this.updateEnvironmentValidators(index);
        this.updateTrailerValidators(index);
      }));
      this.subscriptions.add(environmentControl.valueChanges.subscribe(value => {
        vehicle.quality = value ? value : '';
      }));
      this.subscriptions.add(trailerBodyworkControl.valueChanges.subscribe(value => {
        if (!vehicle.trailer) vehicle.trailer = { bodywork: '', axles: null };
        vehicle.trailer.bodywork = value && value.name ? value.name : '';
        if (!value || !value.name) {
          trailerAxlesControl.setValue(null, { emitEvent: false });
          vehicle.trailer.axles = null;
        }
      }));
      this.subscriptions.add(trailerAxlesControl.valueChanges.subscribe(value => {
        if (!vehicle.trailer) vehicle.trailer = { bodywork: '', axles: null };
        vehicle.trailer.axles = value ? Number(value) : null;
      }));
    });
  }

  private updateEnvironmentValidators(index: number): void {
    const group = this.getVehicleGroup(index);
    const control = group.get('environment') as FormControl;
    if (this.showEnvironmentControl(index)) {
      control.setValidators(Validators.required);
    } else {
      control.clearValidators();
      control.setValue('', { emitEvent: false });
      this.manualCreationCargoService.vehiclesSelected[index].quality = '';
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  private updateTrailerValidators(index: number): void {
    const group = this.getVehicleGroup(index);
    const trailerBodyworkControl = group.get('trailerBodywork') as FormControl;
    const trailerAxlesControl = group.get('trailerAxles') as FormControl;
    if (this.showTrailerInfo(index))
      trailerAxlesControl.setValidators(Validators.min(this.minAxles));
    else {
      trailerAxlesControl.clearValidators();
      trailerAxlesControl.setValue(null, { emitEvent: false });
      this.manualCreationCargoService.vehiclesSelected[index].trailer = { bodywork: '', axles: null };
    }
    trailerBodyworkControl.updateValueAndValidity({ emitEvent: false });
    trailerAxlesControl.updateValueAndValidity({ emitEvent: false });
  }

  setFormControls() {
    this.manualCreationCargoService.vehiclesSelected.forEach((vehicle, index) => {
      this.vehiclesFormArray.push(this.formBuilder.group({
        quantity: [vehicle.quantity ? Number(vehicle.quantity) : 1, [Validators.required, Validators.min(this.minQuantity)]],
        bodyworkType: [vehicle.bodyWorkType && vehicle.bodyWorkType.name ? { name: vehicle.bodyWorkType.name, id: vehicle.bodyWorkType.id } : '', Validators.required],
        environment: [vehicle.quality ? vehicle.quality : ''],
        trailerBodywork: [vehicle.trailer && vehicle.trailer.bodywork ? { name: vehicle.trailer.bodywork } : null],
        trailerAxles: [vehicle.trailer && vehicle.trailer.axles ? Number(vehicle.trailer.axles) : null, Validators.min(this.minAxles)]
      }));
      this.bodyworkOptions[index] = {
        title: 'Tipo de carrocería',
        initialVehicleTypeId: vehicle.id,
        initialVehicleTypeName: vehicle.name,
        initialValue: vehicle.bodyWorkType && vehicle.bodyWorkType.name ? vehicle.bodyWorkType.name : ''
      };
      this.trailerOptions[index] = {
        title: 'Tipo de carrocería del tráiler',
        initialVehicleTypeId: '41',
        initialValue: vehicle.trailer && vehicle.trailer.bodywork ? vehicle.trailer.bodywork : ''
      };
      this.updateEnvironmentValidators(index);
      this.updateTrailerValidators(index);
    });
  }

  getDataVehicle(vehicle: Object) {
    let indexVehicle = _.findIndex(this.manualCreationCargoService.listVehicles, function (vehicleObj) {
      return vehicleObj.id == vehicle['id'];
    });

    if (indexVehicle >= 0) {
      return this.manualCreationCargoService.listVehicles[indexVehicle];
    }
  }

  getIconVehicle(vehicle: Object) {
    return '/assets/svg/icons/icon-' + vehicle['name'].toLowerCase() + '.svg';
  }

  onErrorRenderImage($event: Event) {
    ($event.target as HTMLImageElement).src = '/assets/svg/icons/icon-camion.svg';
  }

  getNameVehicle(vehicle: Object) {
    return this.getDataVehicle(vehicle).name;
  }

  findErrorsVehicles(): boolean {
    for (let i = 0; i < this.vehiclesFormArray.length; i++) {
      const group = this.getVehicleGroup(i);
      if (this.utils.errorMessagesCustomized(group.get('quantity'), `cantidad del vehículo ${i + 1}`, null, null, this.minQuantity)) return false;
      if (this.utils.errorMessagesCustomized(group.get('bodyworkType'), `tipo de carrocería del vehículo ${i + 1}`)) return false;
      if (this.showEnvironmentControl(i) && this.utils.errorMessagesCustomized(group.get('environment'), `tipo de ambiente del vehículo ${i + 1}`)) return false;
      if (this.showTrailerInfo(i) && this.utils.errorMessagesCustomized(group.get('trailerBodywork'), `tipo de carrocería del tráiler del vehículo ${i + 1}`)) return false;
      if (this.showTrailerInfo(i) && this.utils.errorMessagesCustomized(group.get('trailerAxles'), `número de ejes del tráiler del vehículo ${i + 1}`, null, null, this.minAxles)) return false;
    }
    return true;
  }

  nextStep() {
    this.formValidate = 'touched';
    this.vehiclesFormArray.markAllAsTouched();
    this.vehiclesFormArray.controls.forEach((_, index) => {
      this.updateEnvironmentValidators(index);
      this.updateTrailerValidators(index);
    });
    if (this.manualCreationCargoService.isValidVehicleQuantity() && this.vehiclesFormArray.valid && this.findErrorsVehicles()) {
      if (this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.assignLicensePlate)) {
        // Assign vehicles
        this.manualCreationCargoService.getComponentActive().setId(2);
        this.manualCreationCargoService.getComponentActive().getStep().setId(3);
        this.router.navigate([this.manualCreationCargoService.steps.assignVehicles.url]);
      } else {
        const serviceTypeName = this.manualCreationCargoService.cargoForm.controls.cargoModel['controls'].serviceType.controls.name.value;
        if (serviceTypeName === 'Contenedor vacío con carga de compensación' || serviceTypeName === 'Contenedor vacío express' || serviceTypeName === 'Carga consolidada') {
          this.manualCreationCargoService.getComponentActive().setId(2);
          this.manualCreationCargoService.getComponentActive().getStep().setId(5);
          this.router.navigate([this.manualCreationCargoService.steps.optional.url]);
        } else {
          this.manualCreationCargoService.getComponentActive().setId(2);
          this.manualCreationCargoService.getComponentActive().getStep().setId(4);
          this.router.navigate([this.manualCreationCargoService.steps.typeMerchandise.url]);
        }
      }
    } else {
      this.snackBarService.openSnackBar(FormMessages.GENERAL_ERROR_DEFAULT, undefined, 'alert');
    }
  }

  stepBack() {
    this.manualCreationCargoService.getComponentActive().setId(2);
    this.manualCreationCargoService.getComponentActive().getStep().setId(1);
    this.router.navigate([this.manualCreationCargoService.steps.vehicleType.url]);
  }

  ngOnDestroy() {
    if (this.subscriptions) this.subscriptions.unsubscribe();
  }

  get vehiclesFormArray(): FormArray {
    return this.formVehicles.get('vehicles') as FormArray;
  }

  getVehicleGroup(index: number): FormGroup {
    return this.vehiclesFormArray.at(index) as FormGroup;
  }

  getVehicleDisplayName(index: number): string {
    const vehicle = this.manualCreationCargoService.vehiclesSelected[index];
    return vehicle && vehicle.vehicleAlias ? vehicle.vehicleAlias : (vehicle ? vehicle.name : '');
  }

  showEnvironmentControl(index: number): boolean {
    const vehicle = this.manualCreationCargoService.vehiclesSelected[index];
    const vehicleType = vehicle && vehicle.name ? vehicle.name : '';
    const bodyworkType = this.utils.getNestedValue(this.getVehicleGroup(index).get('bodyworkType').value, 'name');
    return this.cargoManager.requestEnvironmentType(vehicleType, bodyworkType);
  }

  showTrailerInfo(index: number): boolean {
    const vehicle = this.manualCreationCargoService.vehiclesSelected[index];
    const vehicleType = vehicle && vehicle.name ? vehicle.name : '';
    const bodyworkType = this.utils.getNestedValue(this.getVehicleGroup(index).get('bodyworkType').value, 'name');
    return vehicleType && vehicleType === 'TRACTOCAMION' && bodyworkType && bodyworkType !== 'GRUA';
  }
}
